import { useEffect, FunctionComponent, useState } from 'react'
import Script from 'next/script'

import config from '@config'

import usePageMetadata from '@hooks/usePageMetadata'
import useUser from '@hooks/useUser'

import { initializePiano, updateExperience } from './skeleton'
import usePianoEvents from './usePianoEvents'
import useSSOTrackingSource from '@hooks/useSSOTrackingSource'
import useDisableThirdPartyScripts from '@hooks/useDisableThirdPartyScripts'

const {
  piano: { pianoSdkUrl, pianoAdBlockDetectionScript },
} = config

// This react component does not have a proper DOM element (just two scripts)
// The logic there is about how to mount third party DOM element and execute DOM element's related function
const Piano: FunctionComponent = () => {
  const disableThirdPartyScripts = useDisableThirdPartyScripts()
  const userData = useUser()
  const ssoSource = useSSOTrackingSource({
    prefix: 'PIA',
    omitPublication: true,
  })
  const { url, teaser } = usePageMetadata()

  const [isPianoSDKLoaded, setIsPianoSDKLoaded] = useState<boolean>(false)

  const pianoEvents = usePianoEvents()

  useEffect(() => {
    // On every route change we have to close templates that were opened
    // by Piano, so that they don't stay open when going back
    // This has to be done on SPA's according to Piano
    window?.tp?.offer?.close()

    // Update the piano experience every time the page metadata changes
    // It needs to be done this way instead of using route change complete
    // because it also needs to update the experience on initial load
    updateExperience({
      currentUrl: `${window.location.origin}${url}`,
      ssoSource,
      isPlus: !!teaser?.isPlus,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, teaser])

  useEffect(() => {
    if (isPianoSDKLoaded) {
      // Initialize Piano as soon as the Piano SDK is loaded
      initializePiano({
        isLoggedIn: !!userData,
        currentUrl: `${window.location.href}`,
        ssoSource,
        pianoEvents,
        isPlus: !!teaser?.isPlus,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPianoSDKLoaded, userData])

  if (disableThirdPartyScripts) {
    return null
  }

  return (
    <>
      <Script id="piano-adblock-detection">
        {pianoAdBlockDetectionScript}
      </Script>
      <Script
        src={pianoSdkUrl}
        strategy="lazyOnload"
        onLoad={() => setIsPianoSDKLoaded(true)}
      />
    </>
  )
}

export default Piano
